import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Clock, Send, CheckCircle2, XCircle, Sparkles, Hourglass } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending: {
    no: "Venter på tilbud",
    en: "Waiting for offer",
    icon: Hourglass, cls: "bg-amber-50 border-amber-200 text-amber-900",
  },
  offered: {
    no: "Tilbud mottatt",
    en: "Offer received",
    icon: Sparkles, cls: "bg-[#BD984D]/10 border-[#BD984D]/40 text-[#0C1824]",
  },
  accepted: {
    no: "Godtatt – behandles",
    en: "Accepted – processing",
    icon: CheckCircle2, cls: "bg-cyan-50 border-cyan-200 text-cyan-900",
  },
  applied: {
    no: "Aktivert",
    en: "Applied",
    icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-300 text-emerald-900",
  },
  declined: {
    no: "Avslått",
    en: "Declined",
    icon: XCircle, cls: "bg-slate-50 border-slate-200 text-slate-700",
  },
};

export default function CustomerTimeChangeRequest({ bookingId, lang = "no", onChanged }) {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    requested_checkin_time: "",
    requested_checkout_time: "",
    customer_note: "",
  });
  const [busy, setBusy] = useState(false);

  const T = lang === "no" ? {
    title: "Endre tid – innsjekk / utsjekk",
    desc: "Ønsker du tidlig innsjekk eller sen utsjekk? Send en søknad så gir vi deg et tilbud (pris avhenger av tilgjengelig personell).",
    ask_btn: "Søk om endrede tider",
    dlg_title: "Søknad om endrede tider",
    dlg_desc: "Fyll inn ønsket klokkeslett – la felt stå tomme for standard. Vi svarer med et tilbud på pris (eller gratis).",
    checkin_l: "Ønsket innsjekk (standard 14:00)",
    checkout_l: "Ønsket utsjekk (standard 10:00)",
    note_l: "Kommentar (valgfritt)",
    note_p: "Vi skal på middag kl. 12, kan vi hente båten kl. 11?",
    cancel: "Avbryt",
    submit: "Send søknad",
    sending: "Sender…",
    offer_from: "Tilbud fra",
    offer_free: "Gratis",
    offer_price: (p) => `${p} kr`,
    accept: "Godta tilbud",
    decline: "Avslå",
    declined_reason_p: "Kort begrunnelse (valgfritt)",
    submitted: "Din søknad er sendt – vi svarer snart.",
    accepted_free: "Godtatt – tidene er aktivert.",
    accepted_paid: "Godtatt – vi tar kontakt for videre oppfølging og betaling.",
    requested: "Ønsket",
  } : {
    title: "Change times – check-in / check-out",
    desc: "Would you like early check-in or late check-out? Submit a request and we'll respond with an offer (price depends on staff availability).",
    ask_btn: "Request time change",
    dlg_title: "Time change request",
    dlg_desc: "Fill in the desired times – leave a field empty for default. We reply with an offer (price or free).",
    checkin_l: "Desired check-in (default 2:00 PM)",
    checkout_l: "Desired check-out (default 10:00 AM)",
    note_l: "Comment (optional)",
    note_p: "We have dinner at 12, can we pick up the boat at 11?",
    cancel: "Cancel",
    submit: "Send request",
    sending: "Sending…",
    offer_from: "Offer from",
    offer_free: "Free",
    offer_price: (p) => `${p} NOK`,
    accept: "Accept offer",
    decline: "Decline",
    declined_reason_p: "Short reason (optional)",
    submitted: "Your request is sent – we'll reply shortly.",
    accepted_free: "Accepted – new times activated.",
    accepted_paid: "Accepted – we'll follow up for the payment step.",
    requested: "Requested",
  };

  const load = async () => {
    if (!bookingId) return;
    try {
      const { data } = await api.get(`/time-change-requests?booking_id=${bookingId}`);
      setRequests(data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, [bookingId]);

  const submit = async () => {
    if (!form.requested_checkin_time && !form.requested_checkout_time) {
      toast.error(lang === "no"
        ? "Fyll inn ønsket innsjekk eller utsjekk (eller begge)."
        : "Fill in desired check-in or check-out time (or both).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/time-change-requests", { booking_id: bookingId, ...form });
      toast.success(T.submitted);
      setForm({ requested_checkin_time: "", requested_checkout_time: "", customer_note: "" });
      setOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally { setBusy(false); }
  };

  const accept = async (req) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/time-change-requests/${req.id}/accept`);
      toast.success(data.status === "applied" ? T.accepted_free : T.accepted_paid);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke godta");
    } finally { setBusy(false); }
  };

  const decline = async (req) => {
    const reason = window.prompt(T.declined_reason_p) ?? "";
    setBusy(true);
    try {
      await api.post(`/time-change-requests/${req.id}/decline`, { reason });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke avslå");
    } finally { setBusy(false); }
  };

  const active = requests.filter((r) => r.status !== "declined" && r.status !== "applied");
  const closed = requests.filter((r) => r.status === "declined" || r.status === "applied");
  const canAsk = active.length === 0;

  return (
    <Card className="p-5 sm:p-6" data-testid="customer-time-change-request">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-lg bg-[#BD984D]/10 text-[#A57F39] shrink-0">
          <Clock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-[#0C1824]">{T.title}</h2>
          <p className="text-sm text-[#2B3F4D] mt-1">{T.desc}</p>

          {canAsk && (
            <Button
              onClick={() => setOpen(true)}
              className="mt-4 bg-[#BD984D] hover:bg-[#A57F39] text-white"
              data-testid="customer-time-change-open"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {T.ask_btn}
            </Button>
          )}

          {active.map((r) => (
            <TimeChangeCard
              key={r.id} req={r} lang={lang} T={T} busy={busy}
              onAccept={() => accept(r)} onDecline={() => decline(r)}
            />
          ))}
          {closed.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-[#A57F39] font-medium">
                {lang === "no" ? "Tidligere søknader" : "Past requests"} ({closed.length})
              </summary>
              <div className="mt-2 space-y-2">
                {closed.map((r) => (
                  <TimeChangeCard key={r.id} req={r} lang={lang} T={T} compact />
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-time-change-dialog">
          <DialogHeader>
            <DialogTitle>{T.dlg_title}</DialogTitle>
            <DialogDescription>{T.dlg_desc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{T.checkin_l}</Label>
              <Input
                type="time"
                value={form.requested_checkin_time}
                onChange={(e) => setForm({ ...form, requested_checkin_time: e.target.value })}
                data-testid="customer-time-change-checkin"
                className="max-w-[180px]"
              />
            </div>
            <div>
              <Label>{T.checkout_l}</Label>
              <Input
                type="time"
                value={form.requested_checkout_time}
                onChange={(e) => setForm({ ...form, requested_checkout_time: e.target.value })}
                data-testid="customer-time-change-checkout"
                className="max-w-[180px]"
              />
            </div>
            <div>
              <Label>{T.note_l}</Label>
              <Textarea
                rows={3}
                value={form.customer_note}
                onChange={(e) => setForm({ ...form, customer_note: e.target.value })}
                placeholder={T.note_p}
                data-testid="customer-time-change-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{T.cancel}</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
              data-testid="customer-time-change-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {busy ? T.sending : T.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TimeChangeCard({ req, lang, T, busy, onAccept, onDecline, compact = false }) {
  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <div
      className={`mt-3 rounded-lg border p-3 sm:p-4 ${meta.cls}`}
      data-testid={`customer-time-change-item-${req.status}`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {lang === "no" ? meta.no : meta.en}
      </div>
      <div className="mt-1 text-sm">
        <strong>{T.requested}:</strong>{" "}
        {req.requested_checkin_time && (
          <span className="mr-2">
            {lang === "no" ? "innsjekk" : "check-in"} {req.requested_checkin_time}
          </span>
        )}
        {req.requested_checkout_time && (
          <span>
            {lang === "no" ? "utsjekk" : "check-out"} {req.requested_checkout_time}
          </span>
        )}
      </div>
      {req.customer_note && !compact && (
        <div className="text-xs mt-1 italic opacity-80">«{req.customer_note}»</div>
      )}
      {req.status === "offered" && !compact && (
        <>
          <div className="mt-3 text-sm font-medium">
            {T.offer_from} {req.offered_by_name || "admin"}:{" "}
            <span className="ml-1 font-bold">
              {req.offer_is_free ? T.offer_free : T.offer_price(req.offer_price_nok || 0)}
            </span>
          </div>
          {req.offer_note && (
            <div className="text-xs mt-1 italic">«{req.offer_note}»</div>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              onClick={onAccept}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              size="sm"
              data-testid={`customer-time-change-accept-${req.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {T.accept}
            </Button>
            <Button
              onClick={onDecline}
              disabled={busy}
              variant="outline"
              size="sm"
              data-testid={`customer-time-change-decline-${req.id}`}
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              {T.decline}
            </Button>
          </div>
        </>
      )}
      {req.status === "declined" && req.declined_reason && (
        <div className="text-xs mt-1 italic">«{req.declined_reason}»</div>
      )}
    </div>
  );
}
